import { usePagination } from '@mantine/hooks';
import { DataTableSortStatus } from 'mantine-datatable';
import { useClientPaginatedTable } from './useClientPaginatedTable';
import { useServerPaginatedTable } from './useServerPaginatedTable';
import { useServerInfiniteTable } from './useServerInfiniteTable';
import { BaseRecord, SortableColumn } from '../types';

export type TableMode = 'client' | 'server';
export type PaginationType = 'paged' | 'infinite';
export type PaginationData = ReturnType<typeof usePagination>;

type UseTableOptions<T> = {
  resource: string;
  mode?: TableMode;
  paginationType?: PaginationType;
  columns?: SortableColumn<T>[];
  searchTerm?: string;
  filterPredicate?: (item: T, search: string) => boolean;
  paginationDependencies?: unknown;
  filters?: Record<string, unknown>;
  pageSize?: number;
  useLocalPagination?: boolean;
  initialSort?: DataTableSortStatus<T>;
};

export type TableData<T> = {
  data: T[];
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  error: unknown;
  pagination?: PaginationData;
  totalRecords: number;
  refetch: () => void;
  sortStatus?: DataTableSortStatus<T>;
  onSortStatusChange?: (status: DataTableSortStatus<T>) => void;
  hasNextPage?: boolean;
  fetchNextPage?: () => void;
  isFetchingNextPage?: boolean;
};

export const useTable = <T extends BaseRecord>(options: UseTableOptions<T>): TableData<T> => {
  const mode = options.mode || 'client';
  const paginationType = options.paginationType || 'paged';
  const pageSize = options.pageSize || 10;

  const isServer = mode === 'server';
  const isInfinite = paginationType === 'infinite';

  const serverTable = useServerPaginatedTable<T>(
    options.resource,
    pageSize,
    options.filters,
    options.initialSort,
    isServer && !isInfinite,
  );
  const serverInfiniteTable = useServerInfiniteTable<T>(
    options.resource,
    pageSize,
    options.filters,
    options.initialSort,
    isServer && isInfinite,
  );
  const clientTable = useClientPaginatedTable<T>({
    ...options,
    columns: options.columns || [],
    searchTerm: options.searchTerm || '',
    pageSize,
    useLocalPagination: options.useLocalPagination || false,
    infinite: isInfinite,
    enabled: !isServer,
  });

  if (isServer) {
    return isInfinite ? serverInfiniteTable : serverTable;
  }
  return clientTable;
};
